import { useMemo, useState } from 'react';
import { AlertTriangle, Eye, EyeOff } from 'lucide-react';
import MallWizard from '../MallWizard';
import StoreCodeGenerator from '../StoreCodeGenerator';
import StoreLocationEditor from '../../../account/components/StoreLocationEditor';
import { GoogleMapsProvider } from '../../../../shared/providers/GoogleMapsProvider';
import './RegisterForm.css';

const RegisterForm = ({
  storeRegister,
  onStoreRegisterFieldChange,
  onSuccess,
  onToast,
  request,
  authToken,
  useStoreAuthResult,
  runWithGlobalLoader,
  isLoading,
  selectedMall,
  onRegisterClick
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPasswordValue, setConfirmPasswordValue] = useState('');

  const {
    registrationStep,
    setRegistrationStep,
    malls,
    cities,
    states,
    selectedCity,
    selectedState,
    setCity,
    setState,
    stateIsOther,
    cityIsOther,
    pincodeValidation,
    areaOptions,
    areaIsOther,
    setAreaIsOther,
    locationReady,
    isPincodeValid,
    showMallForm,
    setShowMallForm,
    mallForm,
    handleMallFormChange,
    handleMallRegister,
    mallSearch,
    setMallSearch,
    handleMallSelect,
    handleMallClear,
    isLoadingMalls
  } = useStoreAuthResult;

  const storeStepTooltip = useMemo(() => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneType = storeRegister.phoneType || 'mobile';
    const phone = String(storeRegister.phone || '').trim();
    const email = String(storeRegister.email || '').trim();

    if (!storeRegister.storeName) return 'Add a name for your business';
    if (!storeRegister.clientType) return 'Select your business type';
    if (!email) return 'Add an email';
    if (!emailPattern.test(email)) return 'Enter a valid email address';
    if (!phone) return 'Add a phone number';
    if (phoneType === 'mobile' && !/^[0-9]{10}$/.test(phone)) return 'Mobile number must be exactly 10 digits';
    if (phoneType === 'landline' && !/^[0-9]{8,11}$/.test(phone)) return 'Landline must be 8–11 digits';
    if (!storeRegister.address || !storeRegister.address.trim()) return 'Set your store address with coordinates';
    if (!storeRegister.latitude || !storeRegister.longitude) return 'Address must have valid coordinates (use autocomplete or geocode button)';
    if (!storeRegister.storeCode) return 'Generate a store code';
    if (!storeRegister.floorNumber) return 'Add a floor number';
    return '';
  }, [storeRegister.address, storeRegister.clientType, storeRegister.email, storeRegister.floorNumber, storeRegister.latitude, storeRegister.longitude, storeRegister.phone, storeRegister.phoneType, storeRegister.storeCode, storeRegister.storeName]);

  const passwordStepTooltip = useMemo(() => {
    const pass = storeRegister.password || '';
    if (!pass || pass.length < 10) return 'Password must be at least 10 characters';
    if (!/[A-Z]/.test(pass)) return 'Password must contain at least one capital letter';
    if (!/[0-9]/.test(pass)) return 'Password must contain at least one number';
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pass)) return 'Password must contain at least one special character';
    if (pass !== confirmPasswordValue) return 'Passwords do not match';
    return '';
  }, [confirmPasswordValue, storeRegister.password]);

  const handleContinueToStore = () => {
    if (!selectedState) {
      onToast?.({ message: 'Please select a state', variant: 'error' });
      return;
    }
    if (!selectedCity) {
      onToast?.({ message: 'Please select a city', variant: 'error' });
      return;
    }
    if (!storeRegister.pincode || String(storeRegister.pincode).length < 6) {
      onToast?.({ message: 'Please enter a valid 6-digit pincode', variant: 'error' });
      return;
    }
    if (!isPincodeValid) {
      onToast?.({ message: 'Please enter a valid pincode that matches your location', variant: 'error' });
      return;
    }
    if (!storeRegister.area) {
      onToast?.({ message: 'Please select an area', variant: 'error' });
      return;
    }
    setRegistrationStep('store');
  };

  return (
    <div className="card register-form-shell">
      <div className="portal-header">
        <h1 className="portal-title">Register Store</h1>
        <p className="portal-subtitle">Configure your digital storefront and start managing your enterprise efficiently with Zopeo.</p>
        <div className="portal-header-accent" />
      </div>

      {registrationStep === 'location' && (
        <MallWizard
          cities={cities}
          states={states}
          malls={malls}
          onSelect={handleMallSelect}
          onCreateNew={() => onStoreRegisterFieldChange('mallUuid', '')}
          selectedState={selectedState}
          selectedCity={selectedCity}
          selectedArea={storeRegister.area}
          selectedPincode={storeRegister.pincode}
          selectedMallUuid={storeRegister.mallUuid}
          selectedMall={selectedMall}
          stateIsOther={stateIsOther}
          cityIsOther={cityIsOther}
          areaIsOther={areaIsOther}
          setState={setState}
          setCity={setCity}
          setAreaIsOther={setAreaIsOther}
          onStateInput={(value) => onStoreRegisterFieldChange('state', value)}
          onCityInput={(value) => onStoreRegisterFieldChange('city', value)}
          onPincodeInput={(value) => onStoreRegisterFieldChange('pincode', value)}
          onAreaInput={(value) => onStoreRegisterFieldChange('area', value)}
          pincodeValidation={pincodeValidation}
          isPincodeValid={isPincodeValid}
          areaOptions={areaOptions}
          locationReady={locationReady}
          showMallForm={showMallForm}
          setShowMallForm={setShowMallForm}
          mallForm={mallForm}
          onMallFormChange={handleMallFormChange}
          onMallRegister={handleMallRegister}
          onMallClear={handleMallClear}
          onContinue={handleContinueToStore}
          isLoadingMalls={isLoadingMalls}
          mallSearch={mallSearch}
          onMallSearch={setMallSearch}
        />
      )}

      {registrationStep === 'store' && (
        <div className="step-block">
          <div className="step-header">
            <h4>Store Details</h4>
            <span className="step-pill">Step 2</span>
          </div>

          <div className="form-grid">
            <div className="input-group">
              <label className="input-label">Business Name</label>
              <input
                className="input"
                value={storeRegister.storeName}
                maxLength={50}
                onChange={(event) => onStoreRegisterFieldChange('storeName', event.target.value)}
              />
            </div>
            <div className="input-group">
              <label className="input-label">Business Type</label>
              <select
                className="input select-input"
                value={storeRegister.clientType || ''}
                onChange={(event) => onStoreRegisterFieldChange('clientType', event.target.value)}
              >
                <option value="" disabled>Select your business type</option>
                <option value="RETAIL">Retail Store</option>
                <option value="CLINIC">Clinic / Hospital</option>
                <option value="SALON">Salon / Spa</option>
                <option value="FOOD_COURT">Food Court</option>
                <option value="SPORTS">Sports / Fitness</option>
                <option value="WELLNESS">Wellness</option>
              </select>
            </div>
            <div className="input-group">
              <label className="input-label">Email</label>
              <input className="input" value={storeRegister.email} maxLength={50} onChange={(event) => onStoreRegisterFieldChange('email', event.target.value)} />
            </div>
            <div className="input-group">
              <label className="input-label">Phone</label>
              <div className="input-group-stacked">
                <select
                  className="input select-input"
                  value={storeRegister.phoneType || 'mobile'}
                  onChange={(event) => onStoreRegisterFieldChange('phoneType', event.target.value)}
                >
                  <option value="mobile">Mobile</option>
                  <option value="landline">Landline</option>
                </select>
                <input
                  className="input"
                  value={storeRegister.phone}
                  maxLength={storeRegister.phoneType === 'landline' ? 11 : 10}
                  onChange={(event) => onStoreRegisterFieldChange('phone', event.target.value.replace(/\D/g, ''))}
                />
              </div>
            </div>

            {storeRegister.mallUuid && selectedMall && (
              <div className="input-group">
                <label className="input-label">Mall</label>
                <input className="input" value={selectedMall?.mallName || ''} disabled />
              </div>
            )}

            <div className="input-group">
              <label className="input-label">City</label>
              <input className="input" value={storeRegister.city} disabled />
            </div>
            <div className="input-group">
              <label className="input-label">Area</label>
              <input className="input" value={storeRegister.area} disabled />
            </div>
            <div className="input-group">
              <label className="input-label">State</label>
              <input className="input" value={storeRegister.state} disabled />
            </div>

            <div className="input-group full-width">
              <GoogleMapsProvider>
                <StoreLocationEditor
                  pincode={storeRegister.pincode}
                  address={storeRegister.address || ''}
                  latitude={storeRegister.latitude}
                  longitude={storeRegister.longitude}
                  onChange={({ address, latitude, longitude }) => {
                    onStoreRegisterFieldChange('address', address);
                    onStoreRegisterFieldChange('latitude', latitude);
                    onStoreRegisterFieldChange('longitude', longitude);
                  }}
                  onValidationChange={({ isValid, error }) => {
                    // Store validation state if needed
                  }}
                  disabled={false}
                  onError={(error) => onToast?.({ message: error, variant: 'error' })}
                />
              </GoogleMapsProvider>
            </div>

            <StoreCodeGenerator
              request={request}
              authToken={authToken}
              storeName={storeRegister.storeName}
              storeCode={storeRegister.storeCode}
              onStoreCodeChange={(code) => onStoreRegisterFieldChange('storeCode', code)}
              onToast={onToast}
              runWithGlobalLoader={runWithGlobalLoader}
            />

            <div className="importance-notice">
              <div className="importance-icon"><AlertTriangle size={16} /></div>
              <div className="importance-text">
                <h6>Store Code is critical</h6>
                <p>This code uniquely identifies your store. You <strong>must</strong> save it, it is required for all the future logins.</p>
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">Floor Number</label>
              <input className="input" value={storeRegister.floorNumber} maxLength={50} onChange={(event) => onStoreRegisterFieldChange('floorNumber', event.target.value)} />
            </div>

            <div className="input-group full-width">
              <label className="input-label">Taxes at Billing</label>
              <label className="tax-toggle-row">
                <input
                  type="checkbox"
                  checked={Boolean(storeRegister.chargesTaxSeparately)}
                  onChange={(event) => onStoreRegisterFieldChange('chargesTaxSeparately', event.target.checked)}
                />
                <span>I charge GST / taxes separately at billing — prices customers see in the app are before tax.</span>
              </label>
              <p className="tax-toggle-hint">
                When enabled, customers see a &ldquo;taxes charged at store&rdquo; note on their appointment bill. Leave it off if your listed prices already include all taxes.
              </p>
            </div>
          </div>

          <div className="step-actions split">
            <button type="button" className="btn ghost" onClick={() => setRegistrationStep('location')}>
              Back
            </button>
            <button
              className="btn primary"
              onClick={() => setRegistrationStep('password')}
              disabled={Boolean(storeStepTooltip)}
              title={storeStepTooltip}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {registrationStep === 'password' && (
        <div className="step-block">
          <div className="step-header">
            <h4>Security</h4>
            <span className="step-pill">Step 3</span>
          </div>
          <div className="form-grid">
            <div className="input-group">
              <label className="input-label">Email</label>
              <input className="input" value={storeRegister.email} disabled />
            </div>
            <div className="input-group">
              <label className="input-label">Store Code</label>
              <input className="input" value={storeRegister.storeCode} disabled />
            </div>

            <div className="input-group">
              <label className="input-label">Password</label>
              <div className="store-code-wrapper">
                <input
                  className="input"
                  type={showPassword ? 'text' : 'password'}
                  value={storeRegister.password || ''}
                  maxLength={128}
                  onChange={(event) => onStoreRegisterFieldChange('password', event.target.value)}
                />
                <button
                  type="button"
                  className="copy-btn"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">Confirm Password</label>
              <div className="store-code-wrapper">
                <input
                  className="input"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPasswordValue}
                  maxLength={128}
                  onChange={(event) => setConfirmPasswordValue(event.target.value)}
                />
                <button
                  type="button"
                  className="copy-btn"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>
          <div className="step-actions split">
            <button type="button" className="btn ghost" onClick={() => setRegistrationStep('store')}>
              Back
            </button>
            <button
              className="btn primary"
              onClick={onRegisterClick}
              disabled={Boolean(passwordStepTooltip) || isLoading}
              title={passwordStepTooltip}
            >
              Create Store
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterForm;
